import React, { useState, useRef, useEffect } from 'react';
import { Bot } from 'lucide-react';
import ChatMessage from './ChatMessage';
import ChatSuggestions from './ChatSuggestions';
import CompanySetupForm from './CompanySetupForm';
import ChatInput from './ChatInput';
import ChatHeader from './ChatHeader';
import { getGekkoMessage, getDepartmentResponse } from '../../utils/gekkoMessages';
import { getOrganizationProfile } from '../../lib/supabase/organization';
import { useJourneyStore } from '../../store/journeyStore';
import { useAuthStore } from '../../store/authStore';

interface Message {
  id: string;
  type: 'user' | 'assistant';
  content: string;
  followUp?: string[];
  timestamp: Date;
}

interface ChatContainerProps {
  department: string;
  gekkoName: string;
  initialMessage?: string;
  initialSuggestions?: string[];
  onResponse?: (response: string) => void;
}

export default function ChatContainer({ 
  department, 
  gekkoName,
  initialMessage,
  initialSuggestions,
  onResponse 
}: ChatContainerProps) {
  const [messages, setMessages] = useState<Message[]>([]);
  const { user } = useAuthStore();
  const [showCompanyForm, setShowCompanyForm] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [hasProfile, setHasProfile] = useState<boolean | null>(null);
  const [isProfileLoading, setIsProfileLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false); 
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  const shouldScrollToBottom = useRef(true);
  const { completeStep } = useJourneyStore();
  const initializationAttempted = useRef(false);
  const profileCheckAttempted = useRef(false);

  // Check profile once on initial load
  useEffect(() => {
    if (profileCheckAttempted.current) return;
    profileCheckAttempted.current = true;
    
    const checkProfile = async () => {
      if (!user) {
        setIsProfileLoading(false);
        setHasProfile(false);
        return;
      }
      
      try {
        setIsProfileLoading(true);
        const profile = await getOrganizationProfile();
        setHasProfile(!!profile);
        if (profile) {
          completeStep('COMPANY_SETUP');
        }
      } catch (error) {
        console.error('Failed to check profile:', error);
        setHasProfile(false);
      } finally {
        setIsProfileLoading(false);
      }
    };
    
    checkProfile();
  }, [completeStep, user]);

  // Set up initial messages only once after profile is loaded
  useEffect(() => {
    if (hasProfile !== null && !isInitialized && !isProfileLoading && !initializationAttempted.current) {
      initializationAttempted.current = true;
      
      const departmentKey = department.toLowerCase().replace(/\s+/g, '-');
      const welcomeMessage = initialMessage || getGekkoMessage(departmentKey).welcome;
      const followUpSuggestions = hasProfile 
        ? (initialSuggestions || getGekkoMessage(departmentKey).followUp) 
        : ["Let's set up my company", "Skip setup for now"];
      
      const initialMsg = {
        id: '1',
        type: 'assistant' as const, 
        content: hasProfile 
          ? welcomeMessage 
          : "Let's start by setting up your company profile. This will help me better assist you with your business needs.",
        followUp: followUpSuggestions,
        timestamp: new Date()
      };
      
      setMessages([initialMsg]);
      setIsInitialized(true);
    }
  }, [hasProfile, isInitialized, department, initialMessage, initialSuggestions, isProfileLoading]);

  // Set up scroll tracking to determine if we should auto-scroll
  useEffect(() => {
    const container = chatContainerRef.current;
    if (!container) return;

    const handleScroll = () => {
      if (container) {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
        shouldScrollToBottom.current = distanceFromBottom < 50;
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
    };
  }, []);

  // Auto-scroll to bottom when messages change if user was at bottom and not actively scrolling
  useEffect(() => {
    if (shouldScrollToBottom.current && messagesEndRef.current && !showCompanyForm) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, showCompanyForm, isTyping]);

  const handleCompanySetupComplete = () => {
    setShowCompanyForm(false);
    setHasProfile(true);
    completeStep('COMPANY_SETUP');

    shouldScrollToBottom.current = true;
    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'assistant',
      content: "Great! I've saved your company information. Let's continue with setting up your business vision.",
      followUp: [
        "Let's work on business vision",
        "What's next?"
      ],
      timestamp: new Date()
    }]);
  };

  const handleSkipSetup = () => {
    setShowCompanyForm(false);
    completeStep('COMPANY_SETUP');
    shouldScrollToBottom.current = true;

    setMessages(prev => [...prev, {
      id: Date.now().toString(),
      type: 'assistant',
      content: "No problem! You can always set up your company profile later. Let's continue to your dashboard.",
      followUp: [
        "What's next?",
        "Show my dashboard"
      ],
      timestamp: new Date()
    }]);
  };

  const handleSendMessage = async (content: string) => {
    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content,
      timestamp: new Date()
    };
    setMessages(prev => [...prev, userMessage]);

    if (onResponse) {
      onResponse(content);
      return;
    }

    shouldScrollToBottom.current = true;
    setIsTyping(true);
    await new Promise(resolve => setTimeout(resolve, 1000));

    // Check for the company setup keywords
    if (content.toLowerCase().includes("set up my company") || 
        content.toLowerCase().includes("setup my company") || 
        content.toLowerCase() === "let's set up my company") {
      setShowCompanyForm(true);
      setIsTyping(false);
      return;
    }
    
    // Check for skip setup request
    if (content.toLowerCase().includes("skip setup") || 
        content.toLowerCase() === "skip for now") {
      handleSkipSetup();
      setIsTyping(false);
      return;
    }

    const response = getDepartmentResponse(
      department.toLowerCase().replace(/\s+/g, '-'),
      content
    );

    const assistantMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: 'assistant',
      content: response.content,
      followUp: response.followUp,
      timestamp: new Date()
    };
    
    setMessages(prev => [...prev, assistantMessage]);
    setIsTyping(false);
  };

  const handleSuggestionClick = (suggestion: string) => {
    handleSendMessage(suggestion);
  };

  if (isProfileLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <ChatHeader department={department} gekkoName={gekkoName} />

      <div 
        ref={chatContainerRef}
        className="flex-1 overflow-y-auto p-4 space-y-4"
      >
        {messages.map((message) => (
          <div key={message.id} className="space-y-4">
            <ChatMessage {...message} />
            {message.type === 'assistant' && message.followUp && (
              <ChatSuggestions
                suggestions={message.followUp}
                onSuggestionClick={handleSuggestionClick}
              />
            )}
          </div>
        ))}
        
        {showCompanyForm && (
          <CompanySetupForm 
            onComplete={handleCompanySetupComplete} 
            onSkip={handleSkipSetup}
          />
        )}

        {isTyping && (
          <div className="flex items-center space-x-2 text-gray-500 mb-2">
            <Bot className="w-5 h-5" />
            <span>Typing...</span>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="p-4 border-t border-gray-100">
        <ChatInput onSendMessage={handleSendMessage} />
      </div>
    </div>
  );
}